import { createSignal, For, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { Camera, Loader2, MapPin, Trash2 } from "@fleet/icons";

import { showToast } from "@/components/ui/toast";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { resolveStorageUrl, uploadStorageObject } from "@/lib/api";
import { captureGeolocation } from "@/lib/capture-geolocation";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import {
  addFieldworkAttachmentMutation,
  type AttachmentRecord,
  deleteFieldworkAttachmentMutation,
  formatAccuracy,
  formatGeoStamp,
  geoMapUrl,
  getAttachmentsByAnswerQuery,
  getAttachmentsByFindingQuery,
  MAX_ATTACHMENT_SIZE_BYTES,
  queryKeys,
  readGeoStamp,
} from "@fieldcheck/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

type PhotoParent =
  | { kind: "answer"; answerId: string; organizationId: string | null }
  | { kind: "finding"; findingId: string; organizationId: string | null };

interface PhotoStripProps {
  parent: PhotoParent;
  readonly?: boolean;
  /** Notifies the parent how many photos there are (materializes result into photo items). */
  onCountChange?: (count: number) => void;
}

/**
 * Photo strip of an answered item or a finding: thumbnails + capture
 * with camera/gallery, using the template's attachments system
 * (upload to S3 + `attachment` row synchronized by PowerSync).
 */
export function PhotoStrip(props: PhotoStripProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const [isUploading, setIsUploading] = createSignal(false);

  const photos = createPowerSyncQuery<AttachmentRecord>({
    queryKey: () =>
      props.parent.kind === "answer"
        ? queryKeys.attachments.byAnswer(props.parent.answerId)
        : queryKeys.attachments.byFinding(props.parent.findingId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return props.parent.kind === "answer"
        ? getAttachmentsByAnswerQuery(dbInstance, props.parent.answerId)
        : getAttachmentsByFindingQuery(dbInstance, props.parent.findingId);
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const notifyCount = (delta: number) => {
    props.onCountChange?.((photos.data?.length ?? 0) + delta);
  };

  const handleUpload = async (e: Event) => {
    const { target } = e;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const file = target.files?.[0];
    target.value = "";
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!file || !dbInstance || !uid) {
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      showToast(t(msg`Photo is too large.`), "error");
      return;
    }
    setIsUploading(true);
    try {
      // GEO-STAMP (proof-of-presence): we request the location IN PARALLEL with
      // the upload. It is best-effort — if the permission is denied or expires, `geo`
      // is null and the photo is uploaded anyway, without a stamp (never blocks evidence).
      const [{ url }, geo] = await Promise.all([uploadStorageObject(file), captureGeolocation()]);
      await addFieldworkAttachmentMutation(dbInstance, {
        userId: uid,
        organizationId: props.parent.organizationId,
        inspectionAnswerId: props.parent.kind === "answer" ? props.parent.answerId : null,
        findingId: props.parent.kind === "finding" ? props.parent.findingId : null,
        filename: file.name,
        url,
        contentType: file.type || "image/jpeg",
        size: file.size,
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        geoAccuracy: geo?.geoAccuracy ?? null,
      });
      notifyCount(1);
    } catch (err) {
      console.error("Error uploading photo:", err);
      showToast(t(msg`Failed to upload photo. It will need to be retried.`), "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    await deleteFieldworkAttachmentMutation(dbInstance, id);
    notifyCount(-1);
  };

  return (
    <div class="flex flex-wrap items-center gap-2">
      <For each={photos.data}>
        {(photo) => {
          const stamp = () => readGeoStamp(photo);
          return (
            <div
              class="group relative h-16 w-16 overflow-hidden rounded-lg border border-border/40 bg-muted"
              data-testid="photo-thumb"
              data-geo={stamp() ? "true" : "false"}
            >
              <img
                src={resolveStorageUrl(photo.url)}
                alt={photo.filename ?? "photo"}
                class="h-full w-full object-cover"
                loading="lazy"
              />
              <Show when={stamp()}>
                {(s) => {
                  const acc = formatAccuracy(s().accuracy);
                  const coords = acc ? `${formatGeoStamp(s())} (${acc})` : formatGeoStamp(s());
                  return (
                    <a
                      href={geoMapUrl(s())}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t(msg`Geotagged: ${coords}`)}
                      aria-label={t(msg`View location on map: ${coords}`)}
                      data-testid="photo-geo-badge"
                      class="absolute left-0.5 top-0.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <MapPin class="h-2.5 w-2.5" />
                    </a>
                  );
                }}
              </Show>
              <Show when={!props.readonly}>
                <button
                  type="button"
                  aria-label={t(msg`Delete photo`)}
                  class="absolute inset-0 hidden items-center justify-center bg-black/50 text-white group-hover:flex"
                  onClick={() => void handleDelete(photo.id)}
                >
                  <Trash2 class="h-4 w-4" />
                </button>
              </Show>
            </div>
          );
        }}
      </For>

      <Show when={!props.readonly}>
        <label
          class="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          data-testid="add-photo"
        >
          <input
            type="file"
            accept="image/*"
            capture="environment"
            class="hidden"
            disabled={isUploading() || !drizzle()}
            onChange={(e) => void handleUpload(e)}
          />
          <Show when={!isUploading()} fallback={<Loader2 class="h-5 w-5 animate-spin" />}>
            <Camera class="h-5 w-5" />
          </Show>
          <span class="text-[9px] font-bold uppercase">{t(msg`Photo`)}</span>
        </label>
      </Show>
    </div>
  );
}
